import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { CheckCircle, AlertCircle, Mail, AlertTriangle } from 'lucide-react'

function Refund() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const sections = [
    {
      title: 'Refund Terms',
      content: [
        {
          text: '• Available for all first-time purchases',
        },
        {
          text: '• Request via email: hireflowapp@gmail.com',
        },
        {
          text: '• Processed within 1-2 business days',
        },
        {
          text: '• One refund per customer',
        },
        {
          text: '• Subject to reasonable usage limits',
        },
      ],
    },
    {
      title: 'Refund Processing',
      content: [
        {
          subtitle: 'Payment Processor',
          text: 'All refunds are issued through Paddle.com, our Merchant of Record and payment processor.',
        },
        {
          subtitle: 'Refund Method',
          text: 'Funds are returned to your original payment method (credit card, PayPal, Apple Pay, or Google Pay).',
        },
        {
          subtitle: 'Processing Time',
          text: 'Refunds typically take 5-10 business days to appear in your account, depending on your bank or payment provider.',
        },
      ],
    },
    {
      title: 'Fair Usage Policy',
      content: [
        {
          subtitle: 'Reasonable Usage Limits',
          text: 'We reserve the right to deny refunds in cases of extraordinary usage or clear abuse patterns, including:',
        },
        {
          text: '• Generating 50+ resumes or cover letters within the 14-day period',
        },
        {
          text: '• Using the service extensively and then requesting a refund',
        },
        {
          text: '• Patterns indicating service abuse or resale',
        },
        {
          text: '• Multiple refund requests across different accounts',
        },
      ],
    },
    {
      title: 'What Counts as Reasonable Usage?',
      content: [
        {
          text: 'We expect most users will:',
        },
        {
          text: '• Generate 5-15 resumes tailored to different jobs',
        },
        {
          text: '• Create a few cover letters',
        },
        {
          text: '• Experiment with different templates and formats',
        },
        {
          text: '• Use the ATS scoring feature on multiple resumes',
        },
        {
          subtitle: 'This is completely fine!',
          text: 'Our fair usage policy only targets extreme cases where customers clearly abuse the service.',
        },
      ],
    },
    {
      title: 'For Monthly Subscribers',
      content: [
        {
          subtitle: 'Simply Cancel Anytime',
          text: 'We don\'t charge for unused time after cancellation. You\'ll retain access until the end of your current billing period.',
        },
        {
          text: '• Cancel through your account settings',
        },
        {
          text: '• No penalties or fees for cancellation',
        },
        {
          text: '• Access continues until subscription period ends',
        },
        {
          text: '• Can request refund within 14 days of purchase',
        },
      ],
    },
    {
      title: 'Cancellation vs Refund',
      content: [
        {
          text: 'Cancellation stops future charges but maintains current access.',
        },
        {
          text: 'Refund returns your payment and immediately revokes premium access.',
        },
        {
          text: 'Choose cancellation if you want to finish your current billing period. Choose refund if you\'re within 14 days and want your money back.',
        },
      ],
    },
    {
      title: 'Chargebacks',
      content: [
        {
          text: 'Please contact us before filing a chargeback with your bank or credit card company. We\'re happy to issue refunds through our normal process, which is faster and easier for both parties.',
        },
        {
          subtitle: 'Chargebacks filed without contacting us may result in:',
          text: '• Permanent account termination • Ban from future service use • Referral to collections for service costs',
        },
      ],
    },
    {
      title: 'Lifetime License Refunds',
      content: [
        {
          text: '• Same 14-day money-back guarantee applies',
        },
        {
          text: '• Full refund if requested within 14 days',
        },
        {
          text: '• License immediately revoked upon refund',
        },
        {
          text: '• Subject to same fair usage limits',
        },
      ],
    },
    {
      title: 'Why 14 Days?',
      content: [
        {
          subtitle: '14 days is plenty of time to:',
          text: '• Create multiple job-matched resumes • Test all premium templates • Use AI optimization features • Generate cover letters • Apply to jobs and see results',
        },
        {
          text: 'Most users know within the first week whether HireFlow is right for them. Our 14-day window is competitive, protective, and gives you ample time to evaluate the service.',
        },
      ],
    },
  ]

  const refundSteps = [
    {
      number: 1,
      title: 'Email Us',
      description: 'Send an email to hireflowapp@gmail.com with "Refund Request" in the subject line',
    },
    {
      number: 2,
      title: 'Include Details',
      description: 'Provide your email address and order number (if available)',
    },
    {
      number: 3,
      title: 'We Process',
      description: 'We\'ll process your refund within 1-2 business days',
    },
  ]

  return (
    <>
      <Helmet>
        <title>Refund Policy - HireFlow</title>
        <meta
          name="description"
          content="HireFlow offers a 14-day money-back guarantee on all paid plans. Learn about our straightforward refund policy and how to request a refund."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-neutral-50 to-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="heading-xl mb-6">Refund Policy</h1>
            <p className="text-lg text-neutral-600 mb-4">
              <strong>Last Updated:</strong> November 22, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* 14-Day Guarantee Highlight */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="card p-8 bg-green-50 border-2 border-green-200 mb-16"
            >
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold text-green-900 mb-2">
                    ✓ 14-Day Money-Back Guarantee
                  </h2>
                  <p className="text-lg text-green-800">
                    We're confident HireFlow will help you land more interviews. 
                    If you're not satisfied within 14 days of purchase, we'll refund you—no questions asked.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Refund Steps */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <h2 className="text-2xl font-bold text-neutral-900 mb-8 text-center">
                How to Request a Refund
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {refundSteps.map((step, index) => (
                  <div
                    key={index}
                    className="card p-6 text-center"
                  >
                    <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-neutral-600 text-sm">
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Content Sections */}
            <div className="space-y-12">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-100px' }}
                  variants={fadeInUp}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-bold text-primary-600 mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-4">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        {item.subtitle && (
                          <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                            {item.subtitle}
                          </h3>
                        )}
                        <p className="text-neutral-600 leading-relaxed whitespace-pre-line">
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Fair Usage Warning */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12 card p-6 bg-yellow-50 border-2 border-yellow-200"
            >
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                    ⚠️ Reasonable Usage Limits
                  </h3>
                  <p className="text-yellow-800">
                    We reserve the right to deny refunds in cases of extraordinary usage (e.g., 50+ resumes generated, clear abuse patterns). 
                    This policy ensures fair access for all customers while preventing abuse.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Chargeback Warning */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12 card p-6 bg-red-50 border-2 border-red-200"
            >
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900 mb-2">
                    Important: Please Contact Us Before Filing a Chargeback
                  </h3>
                  <p className="text-red-800">
                    We're happy to issue refunds through our normal process, which is faster and easier for both parties. 
                    Chargebacks can result in account suspension and may affect your ability to use our services in the future.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Box */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12 card p-8 bg-neutral-900 text-center"
            >
              <Mail className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">
                Questions About Refunds?
              </h3>
              <p className="text-neutral-300 mb-4">
                Contact us at:
              </p>
              <a
                href="mailto:hireflowapp@gmail.com"
                className="text-2xl font-semibold text-green-400 hover:text-green-300 transition-colors"
              >
                hireflowapp@gmail.com
              </a>
              <p className="text-sm text-neutral-400 mt-4">
                We typically respond within 24 hours on business days
              </p>
            </motion.div>

            {/* Related Links */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12"
            >
              <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                Related Policies
              </h3>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/terms"
                  className="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Terms of Service
                </a>
                <a
                  href="/privacy"
                  className="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Privacy Policy
                </a>
                <a
                  href="/pricing"
                  className="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Pricing Plans
                </a>
              </div>
            </motion.div>

            {/* Thank You Box */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="mt-12 card p-8 bg-primary-50 border-2 border-primary-200"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-primary-900 mb-3">
                  💙 We're Here to Help You Succeed
                </h3>
                <p className="text-lg text-primary-800">
                  Our goal is to help you land your dream job. We're confident HireFlow will deliver value within the first 2 weeks. Try it risk-free today!
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Refund
